import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sidebar } from '@/components/Sidebar';
import { Plus, ArrowLeft, Trash2, Palette, Link2, LayoutGrid, Maximize2, GripHorizontal, X, MessageCircle, FileText, Send, ChevronDown, ChevronUp, Type, Minus, PenLine } from 'lucide-react';

const COLORS = {
  default: { bg: 'bg-stone-800/80', border: 'border-stone-700', text: 'text-stone-200' },
  blue: { bg: 'bg-sky-900/40', border: 'border-sky-800/50', text: 'text-sky-200' },
  green: { bg: 'bg-green-900/40', border: 'border-green-800/50', text: 'text-green-200' },
  amber: { bg: 'bg-amber-900/40', border: 'border-amber-800/50', text: 'text-amber-200' },
  red: { bg: 'bg-red-900/40', border: 'border-red-800/50', text: 'text-red-200' },
  violet: { bg: 'bg-violet-900/40', border: 'border-violet-800/50', text: 'text-violet-200' },
};

export default function PatientBoardPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const boardRef = useRef(null);
  const [patient, setPatient] = useState(null);
  const [cards, setCards] = useState([]);
  const [connections, setConnections] = useState([]);
  const [boardSettings, setBoardSettings] = useState({ view_mode: 'freeform', columns: [] });
  const [loading, setLoading] = useState(true);
  const [editCard, setEditCard] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [connecting, setConnecting] = useState(null); // card id being connected FROM
  const [dragCard, setDragCard] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // Topic chat
  const [topicCardId, setTopicCardId] = useState(null);
  const [topicMessages, setTopicMessages] = useState([]);
  const [topicInput, setTopicInput] = useState('');
  // Document templates
  const [docsOpen, setDocsOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [tplEditorOpen, setTplEditorOpen] = useState(false);


  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, connsRes, settingsRes] = await Promise.all([
        api.get(`/patient-board/cards?patient_id=${patientId}`),
        api.get(`/patient-board/connections?patient_id=${patientId}`),
        api.get(`/patient-board/settings?patient_id=${patientId}`),
      ]);
      setCards(cardsRes.data);
      setConnections(connsRes.data);
      setBoardSettings(settingsRes.data);
    } catch {}
    setLoading(false);
  }, [patientId]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    // Get patient info
    api.get(`/patients`).then(({ data }) => {
      const p = data.find(pt => pt.id === patientId);
      if (p) setPatient(p);
    }).catch(() => {});
    fetchData();
  }, [patientId, navigate, fetchData]);

  // Create card
  const addCard = async (x = 150, y = 150) => {
    try {
      const { data } = await api.post('/patient-board/cards', { patient_id: patientId, title: 'Новая карточка', x, y });
      setCards(prev => [...prev, data]);
    } catch { toast.error('Ошибка'); }
  };

  // Topic chat
  const openTopic = async (cardId) => {
    setTopicCardId(cardId);
    setTopicInput('');
    try {
      const { data } = await api.get(`/patient-board/topics/${cardId}/messages`);
      setTopicMessages(data);
    } catch { setTopicMessages([]); }
  };
  const sendTopicMessage = async () => {
    if (!topicInput.trim() || !topicCardId) return;
    try {
      const { data } = await api.post(`/patient-board/topics/${topicCardId}/messages`, { text: topicInput });
      setTopicMessages(prev => [...prev, data]);
      setTopicInput('');
    } catch { toast.error('Ошибка'); }
  };

  // Templates
  const fetchTemplates = async () => {
    try { const { data } = await api.get('/patient-board/templates'); setTemplates(data); } catch {}
  };
  const createTemplate = async () => {
    const tpl = { name: 'Новый шаблон', blocks: [{ type: 'heading', content: '{клиника}' }, { type: 'text', content: 'Клиент: {имя_клиента}\nДата: {дата}' }, { type: 'separator' }, { type: 'text', content: '' }, { type: 'signature', content: '{специалист}' }] };
    try { const { data } = await api.post('/patient-board/templates', tpl); setTemplates(prev => [...prev, data]); setEditTemplate(data); setTplEditorOpen(true); } catch { toast.error('Ошибка'); }
  };
  const saveTemplate = async () => {
    if (!editTemplate) return;
    try { await api.put(`/patient-board/templates/${editTemplate.id}`, { name: editTemplate.name, blocks: editTemplate.blocks }); setTemplates(prev => prev.map(t => t.id === editTemplate.id ? editTemplate : t)); setTplEditorOpen(false); toast.success('Шаблон сохранён'); } catch { toast.error('Ошибка'); }
  };
  const generateDoc = async (tplId) => {
    try {
      const resp = await api.post(`/patient-board/templates/${tplId}/generate`, { patient_id: patientId }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = `document_${patientId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Документ сгенерирован');
    } catch { toast.error('Ошибка генерации'); }
  };


  // Update card
  const saveCard = async () => {
    if (!editCard) return;
    try {
      await api.put(`/patient-board/cards/${editCard.id}`, editCard);
      setCards(prev => prev.map(c => c.id === editCard.id ? { ...c, ...editCard } : c));
      setEditOpen(false);
    } catch { toast.error('Ошибка'); }
  };

  // Delete card
  const deleteCard = async (id) => {
    try {
      await api.delete(`/patient-board/cards/${id}`);
      setCards(prev => prev.filter(c => c.id !== id));
      setConnections(prev => prev.filter(c => c.from_card_id !== id && c.to_card_id !== id));
    } catch { toast.error('Ошибка'); }
  };

  // Create connection
  const finishConnection = async (toId) => {
    if (!connecting || connecting === toId) { setConnecting(null); return; }
    try {
      const { data } = await api.post('/patient-board/connections', { patient_id: patientId, from_card_id: connecting, to_card_id: toId, style: 'arrow' });
      setConnections(prev => [...prev, data]);
    } catch { toast.error('Ошибка'); }
    setConnecting(null);
  };

  // Delete connection
  const deleteConnection = async (id) => {
    try {
      await api.delete(`/patient-board/connections/${id}`);
      setConnections(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  // Drag card position
  const handleMouseDown = (e, card) => {
    if (e.target.closest('button') || e.target.closest('textarea') || e.target.closest('input')) return;
    e.preventDefault();
    const rect = boardRef.current.getBoundingClientRect();
    setDragCard(card.id);
    setDragOffset({ x: e.clientX - rect.left - card.x, y: e.clientY - rect.top - card.y });
  };
  const handleMouseMove = (e) => {
    if (!dragCard) return;
    const rect = boardRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragOffset.x);
    const y = Math.max(0, e.clientY - rect.top - dragOffset.y);
    setCards(prev => prev.map(c => c.id === dragCard ? { ...c, x, y } : c));
  };
  const handleMouseUp = async () => {
    if (!dragCard) return;
    const card = cards.find(c => c.id === dragCard);
    if (card) {
      try { await api.put(`/patient-board/cards/${dragCard}`, { x: card.x, y: card.y }); } catch {}
    }
    setDragCard(null);
  };

  // Toggle kanban
  const toggleView = async () => {
    const newMode = boardSettings.view_mode === 'freeform' ? 'kanban' : 'freeform';
    const newCols = newMode === 'kanban' && boardSettings.columns?.length === 0
      ? [{ id: 'plan', name: 'План' }, { id: 'progress', name: 'В работе' }, { id: 'done', name: 'Завершено' }]
      : boardSettings.columns;
    setBoardSettings(s => ({ ...s, view_mode: newMode, columns: newCols }));
    await api.put('/patient-board/settings', { patient_id: patientId, view_mode: newMode, columns: newCols }).catch(() => {});
  };

  // SVG connections
  const getCardCenter = (cardId) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return { x: 0, y: 0 };
    return { x: card.x + (card.width || 200) / 2, y: card.y + 40 };
  };

  const isKanban = boardSettings.view_mode === 'kanban';

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-6 pt-16 lg:pt-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/patients')} className="text-stone-400"><ArrowLeft size={16} /></Button>
            <div>
              <h1 className="font-heading text-xl text-stone-200">{patient?.name || 'Дашборд клиента'}</h1>
              {patient?.phone && <p className="text-stone-500 text-xs">{patient.phone}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { setDocsOpen(true); fetchTemplates(); }}
              className="border-stone-700 text-stone-400 text-xs">
              <FileText size={14} className="mr-1" /> Документы
            </Button>
            <Button size="sm" variant="outline" onClick={toggleView}
              className="border-stone-700 text-stone-400 text-xs">
              {isKanban ? <Maximize2 size={14} className="mr-1" /> : <LayoutGrid size={14} className="mr-1" />}
              {isKanban ? 'Свободная доска' : 'Канбан'}
            </Button>
            <Button size="sm" onClick={() => addCard(100 + Math.random() * 300, 100 + Math.random() * 200)}
              className="bg-amber-700 hover:bg-amber-600 text-white text-xs">
              <Plus size={14} className="mr-1" /> Карточка
            </Button>
          </div>
        </div>

        {connecting && (
          <div className="bg-violet-900/20 border border-violet-800/30 rounded-lg px-3 py-1.5 mb-2 text-violet-400 text-xs flex items-center gap-2">
            <Link2 size={14} /> Кликните на карточку для создания связи
            <button onClick={() => setConnecting(null)} className="ml-auto text-stone-500 hover:text-red-400"><X size={14} /></button>
          </div>
        )}

        {loading ? <p className="text-stone-500 text-center py-12">Загрузка...</p> : isKanban ? (
          /* ── Kanban View ── */
          <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 140px)' }}>
            {(boardSettings.columns || []).map(col => (
              <div key={col.id} className="shrink-0 w-72 rounded-xl bg-stone-900/50 border border-stone-800/50 p-3">
                <h3 className="text-stone-300 text-sm font-medium mb-3">{col.name}</h3>
                <div className="space-y-2 min-h-[100px]"
                  onDragOver={e => e.preventDefault()}
                  onDrop={async e => {
                    const cardId = e.dataTransfer.getData('cardId');
                    if (cardId) {
                      await api.put(`/patient-board/cards/${cardId}`, { column: col.id });
                      setCards(prev => prev.map(c => c.id === cardId ? { ...c, column: col.id } : c));
                    }
                  }}>
                  {cards.filter(c => c.column === col.id).map(card => {
                    const clr = COLORS[card.color] || COLORS.default;
                    return (
                      <div key={card.id} draggable
                        onDragStart={e => e.dataTransfer.setData('cardId', card.id)}
                        onClick={() => connecting ? finishConnection(card.id) : null}
                        className={`${clr.bg} ${clr.border} border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:brightness-110 transition-all ${connecting ? 'ring-1 ring-violet-600 cursor-pointer' : ''}`}>
                        <p className={`text-sm font-medium ${clr.text}`}>{card.title || 'Без названия'}</p>
                        {card.content && <p className="text-stone-400 text-xs mt-1 line-clamp-2">{card.content}</p>}
                        <div className="flex items-center gap-1 mt-2">
                          <button onClick={e => { e.stopPropagation(); setEditCard({ ...card }); setEditOpen(true); }} className="text-stone-600 hover:text-amber-400 text-[10px]">Изменить</button>
                          <button onClick={e => { e.stopPropagation(); setConnecting(card.id); }} className="text-stone-600 hover:text-violet-400 text-[10px]">Связь</button>
                          <button onClick={e => { e.stopPropagation(); deleteCard(card.id); }} className="text-stone-600 hover:text-red-400 text-[10px] ml-auto">Удалить</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Unassigned cards */}
            <div className="shrink-0 w-72 rounded-xl bg-stone-900/30 border border-dashed border-stone-800/30 p-3">
              <h3 className="text-stone-600 text-sm mb-3">Без колонки</h3>
              <div className="space-y-2">
                {cards.filter(c => !c.column || !(boardSettings.columns || []).some(col => col.id === c.column)).map(card => {
                  const clr = COLORS[card.color] || COLORS.default;
                  return (
                    <div key={card.id} draggable onDragStart={e => e.dataTransfer.setData('cardId', card.id)}
                      className={`${clr.bg} ${clr.border} border rounded-lg p-3 cursor-grab`}>
                      <p className={`text-sm font-medium ${clr.text}`}>{card.title}</p>
                      <button onClick={() => { setEditCard({ ...card }); setEditOpen(true); }} className="text-stone-600 hover:text-amber-400 text-[10px] mt-1">Изменить</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* ── Freeform Board ── */
          <div ref={boardRef} className="relative rounded-xl border border-stone-800/50 overflow-hidden"
            style={{ minHeight: 'calc(100vh - 140px)', background: 'var(--app-card-deep)', cursor: dragCard ? 'grabbing' : 'default' }}
            onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
            onDoubleClick={e => { const rect = boardRef.current.getBoundingClientRect(); addCard(e.clientX - rect.left - 100, e.clientY - rect.top - 20); }}>

            {/* Grid dots */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10">
              <defs><pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="15" cy="15" r="1" fill="currentColor" className="text-stone-500" />
              </pattern></defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>

            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
              {connections.map(conn => {
                const from = getCardCenter(conn.from_card_id);
                const to = getCardCenter(conn.to_card_id);
                const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
                return (
                  <g key={conn.id}>
                    <line x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                      stroke={conn.style === 'dashed' ? '#6b7280' : '#8b5cf6'} strokeWidth="2"
                      strokeDasharray={conn.style === 'dashed' ? '6,4' : 'none'}
                      markerEnd={conn.style === 'arrow' ? 'url(#arrowhead)' : ''} />
                    {conn.label && (
                      <text x={mid.x} y={mid.y - 6} textAnchor="middle" fill="#9ca3af" fontSize="10">{conn.label}</text>
                    )}
                    {/* Clickable delete zone */}
                    <circle cx={mid.x} cy={mid.y} r="8" fill="transparent" className="pointer-events-auto cursor-pointer"
                      onClick={() => deleteConnection(conn.id)} />
                    <circle cx={mid.x} cy={mid.y} r="3" fill="#4b5563" className="pointer-events-none" />
                  </g>
                );
              })}
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#8b5cf6" />
                </marker>
              </defs>
            </svg>

            {/* Cards */}
            {cards.map(card => {
              const clr = COLORS[card.color] || COLORS.default;
              return (
                <div key={card.id}
                  className={`absolute ${clr.bg} ${clr.border} border rounded-xl p-3 shadow-lg transition-shadow hover:shadow-xl z-10 ${connecting ? 'cursor-pointer ring-1 ring-violet-600/50' : ''} ${dragCard === card.id ? 'opacity-80 scale-[1.02]' : ''}`}
                  style={{ left: card.x, top: card.y, width: card.width || 200, minHeight: 60 }}
                  onMouseDown={e => !connecting && handleMouseDown(e, card)}
                  onClick={() => connecting && finishConnection(card.id)}>
                  {/* Drag handle */}
                  <div className="flex items-center justify-between mb-1">
                    <GripHorizontal size={12} className="text-stone-600 cursor-grab" />
                    <div className="flex gap-0.5">
                      <button onClick={e => { e.stopPropagation(); setConnecting(card.id); }} className="text-stone-600 hover:text-violet-400 p-0.5" title="Связать"><Link2 size={10} /></button>
                      <button onClick={e => { e.stopPropagation(); openTopic(card.id); }} className="text-stone-600 hover:text-sky-400 p-0.5" title="Чат"><MessageCircle size={10} /></button>
                      <button onClick={e => { e.stopPropagation(); setEditCard({ ...card }); setEditOpen(true); }} className="text-stone-600 hover:text-amber-400 p-0.5" title="Изменить"><Palette size={10} /></button>
                      <button onClick={e => { e.stopPropagation(); deleteCard(card.id); }} className="text-stone-600 hover:text-red-400 p-0.5" title="Удалить"><Trash2 size={10} /></button>
                    </div>
                  </div>
                  <p className={`text-sm font-medium ${clr.text}`}>{card.title || 'Без названия'}</p>
                  {card.content && <p className="text-stone-400 text-xs mt-1 whitespace-pre-wrap line-clamp-6">{card.content}</p>}
                </div>
              );
            })}

            {cards.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <p className="text-stone-600 text-sm">Дважды кликните для создания карточки</p>
                  <p className="text-stone-700 text-xs mt-1">или нажмите «+ Карточка»</p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Edit Card Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Карточка</DialogTitle></DialogHeader>
          {editCard && (
            <div className="space-y-3">
              <Input value={editCard.title} onChange={e => setEditCard({ ...editCard, title: e.target.value })}
                placeholder="Заголовок" className="bg-stone-950/50 border-stone-800 text-stone-200" />
              <Textarea value={editCard.content} onChange={e => setEditCard({ ...editCard, content: e.target.value })}
                placeholder="Содержание..." rows={4} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm resize-none" />
              <div>
                <label className="text-stone-500 text-[10px] mb-1.5 block">Цвет</label>
                <div className="flex gap-2">
                  {Object.keys(COLORS).map(c => (
                    <button key={c} onClick={() => setEditCard({ ...editCard, color: c })}
                      className={`w-7 h-7 rounded-md border-2 transition-all ${COLORS[c].bg} ${editCard.color === c ? 'border-amber-500 scale-110' : 'border-transparent'}`} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditOpen(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button onClick={saveCard} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topic Chat Panel */}
      {topicCardId && (
        <div className="fixed right-4 bottom-4 w-80 bg-stone-900 border border-stone-800 rounded-xl shadow-2xl z-50 flex flex-col" style={{ maxHeight: '400px' }}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-stone-800">
            <span className="text-stone-200 text-sm font-medium flex items-center gap-1.5">
              <MessageCircle size={14} className="text-sky-400" />
              {cards.find(c => c.id === topicCardId)?.title || 'Чат'}
            </span>
            <button onClick={() => setTopicCardId(null)} className="text-stone-500 hover:text-red-400"><X size={14} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" style={{ minHeight: 120, maxHeight: 280 }}>
            {topicMessages.length === 0 && <p className="text-stone-600 text-xs text-center py-4">Нет сообщений</p>}
            {topicMessages.map(m => (
              <div key={m.id} className="p-2 rounded-lg bg-stone-800/50">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-amber-400 text-[10px] font-medium">{m.sender_name}</span>
                  <span className="text-stone-600 text-[9px]">{new Date(m.created_at).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                </div>
                <p className="text-stone-300 text-xs">{m.text}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-1.5 p-2 border-t border-stone-800">
            <Input value={topicInput} onChange={e => setTopicInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendTopicMessage()}
              placeholder="Сообщение..." className="bg-stone-950/50 border-stone-800 text-stone-200 text-xs h-8 flex-1" />
            <Button size="sm" onClick={sendTopicMessage} className="bg-sky-700 hover:bg-sky-600 text-white h-8 w-8 p-0"><Send size={14} /></Button>
          </div>
        </div>
      )}

      {/* Documents Dialog */}
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200 flex items-center gap-2"><FileText size={18} /> Шаблоны документов</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button size="sm" onClick={createTemplate} className="bg-amber-700 hover:bg-amber-600 text-white text-xs">
              <Plus size={14} className="mr-1" /> Новый шаблон
            </Button>
            {templates.length === 0 && <p className="text-stone-600 text-sm text-center py-4">Нет шаблонов</p>}
            {templates.map(tpl => (
              <div key={tpl.id} className="flex items-center justify-between p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                <div>
                  <p className="text-stone-200 text-sm font-medium">{tpl.name}</p>
                  <p className="text-stone-600 text-xs">{(tpl.blocks || []).length} блоков</p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => { setEditTemplate({ ...tpl }); setTplEditorOpen(true); }}
                    className="border-stone-700 text-stone-400 text-xs h-7">Редактировать</Button>
                  <Button size="sm" onClick={() => generateDoc(tpl.id)}
                    className="bg-green-800 hover:bg-green-700 text-white text-xs h-7">PDF</Button>
                  <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/patient-board/templates/${tpl.id}`); setTemplates(prev => prev.filter(t => t.id !== tpl.id)); }}
                    className="text-stone-600 hover:text-red-400 h-7 w-7 p-0"><Trash2 size={13} /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={tplEditorOpen} onOpenChange={setTplEditorOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Конструктор шаблона</DialogTitle>
          </DialogHeader>
          {editTemplate && (
            <div className="space-y-3">
              <Input value={editTemplate.name} onChange={e => setEditTemplate({ ...editTemplate, name: e.target.value })}
                placeholder="Название шаблона" className="bg-stone-950/50 border-stone-800 text-stone-200 font-medium" />
              <p className="text-stone-600 text-[10px]">Переменные: {'{имя_клиента}'} {'{телефон}'} {'{email}'} {'{дата}'} {'{клиника}'} {'{специалист}'}</p>
              <div className="space-y-2">
                {(editTemplate.blocks || []).map((block, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-stone-950/30 border border-stone-800/50">
                    <div className="flex flex-col gap-0.5 shrink-0 pt-1">
                      {i > 0 && <button onClick={() => { const b = [...editTemplate.blocks]; [b[i-1], b[i]] = [b[i], b[i-1]]; setEditTemplate({...editTemplate, blocks: b}); }} className="text-stone-600 hover:text-stone-300"><ChevronUp size={12} /></button>}
                      {i < editTemplate.blocks.length - 1 && <button onClick={() => { const b = [...editTemplate.blocks]; [b[i], b[i+1]] = [b[i+1], b[i]]; setEditTemplate({...editTemplate, blocks: b}); }} className="text-stone-600 hover:text-stone-300"><ChevronDown size={12} /></button>}
                    </div>
                    <div className="flex-1 space-y-1">
                      <select value={block.type} onChange={e => { const b = [...editTemplate.blocks]; b[i] = {...b[i], type: e.target.value}; setEditTemplate({...editTemplate, blocks: b}); }}
                        className="bg-stone-950 border border-stone-800 text-stone-400 text-xs rounded px-2 py-1">
                        <option value="heading">Заголовок</option>
                        <option value="subheading">Подзаголовок</option>
                        <option value="text">Текст</option>
                        <option value="separator">Разделитель</option>
                        <option value="signature">Подпись</option>
                        <option value="date_field">Поле даты</option>
                      </select>
                      {!['separator', 'date_field'].includes(block.type) && (
                        <Textarea value={block.content} onChange={e => { const b = [...editTemplate.blocks]; b[i] = {...b[i], content: e.target.value}; setEditTemplate({...editTemplate, blocks: b}); }}
                          rows={block.type === 'text' ? 3 : 1} className="bg-stone-950/50 border-stone-800 text-stone-200 text-xs resize-none" />
                      )}
                    </div>
                    <button onClick={() => { const b = editTemplate.blocks.filter((_, j) => j !== i); setEditTemplate({...editTemplate, blocks: b}); }}
                      className="text-stone-600 hover:text-red-400 shrink-0 pt-1"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { type: 'heading', label: 'Заголовок', icon: Type },
                  { type: 'text', label: 'Текст', icon: Type },
                  { type: 'separator', label: 'Разделитель', icon: Minus },
                  { type: 'signature', label: 'Подпись', icon: PenLine },
                  { type: 'date_field', label: 'Дата', icon: FileText },
                ].map(btn => (
                  <Button key={btn.type} size="sm" variant="outline"
                    onClick={() => setEditTemplate({ ...editTemplate, blocks: [...editTemplate.blocks, { type: btn.type, content: '' }] })}
                    className="border-stone-700 text-stone-400 text-xs h-7">
                    <btn.icon size={12} className="mr-1" /> {btn.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTplEditorOpen(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button onClick={saveTemplate} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
